// src/pages/NewConnectorWizard.jsx - FULLY INTEGRATED
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Card, CardContent } from '@/components/ui/card.jsx';

// Import step components
import StepIndicator from './wizard/StepIndicator';
import Step1ChooseConnector from './wizard/Step1ChooseConnector';
import Step2Authenticate from './wizard/Step2Authenticate';
import Step3AccessScope from './wizard/Step3AccessScope';
import Step4Entities from './wizard/Step4Entities';
import Step5Review from './wizard/Step5Review';


export default function NewConnectorWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    connector: null,
    configId: null,        // set in Step 2 once config is created
    authMethod: null,      // 'oauth' | 'pat'
    authComplete: false,   // true when OAuth/PAT validated
    auth: {},
    scope: [],
    entities: [],
  });


  const steps = [
    { number: 1, title: 'Choose Connector' },
    { number: 2, title: 'Authenticate' },
    { number: 3, title: 'Access & Scope' },
    { number: 4, title: 'Entities & Fields' },
    { number: 5, title: 'Review & Connect' },
  ];

  const handleNext = () => {
    if (currentStep < 5 && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/connectors');
    }
  };

  const handleConnect = (connectorData, syncResults) => {
    console.log('Connector created:', connectorData);
    console.log('Sync results:', syncResults);
    
    // Navigate back to connectors page
    navigate('/connectors');
  };

  const handleError = (error) => {
    console.error('Connection error:', error);
    // Error is already shown in Step5, just log it
  };

  const updateWizardData = (field, value) => {
    setWizardData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // Must select an active connector (not coming soon)
        return wizardData.connector !== null && wizardData.connector.status === 'active';


      
      case 2:
        // Auth is complete when OAuth succeeded OR a PAT was tested successfully
        return wizardData.authComplete === true && wizardData.configId !== null;

      
      case 3:
        // GitHub: must pick an organization/account.
        // Other connectors: scope is determined by OAuth, always allow proceeding.
        const k = (wizardData.connector?.id || wizardData.connector?.key || '').toLowerCase();
        if (k === 'github') {
          return Array.isArray(wizardData.scope) && wizardData.scope.length > 0;
        }
        return true;

      
      case 4:
        // Must select at least one entity
        return wizardData.entities.length > 0;
      
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/connectors')}
            className="flex items-center gap-2 text-sm text-[#a0a0a0] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Connectors
          </button>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            New Connector
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            Connect a new data source to Connex
          </p>
        </div>

        {/* Step Indicator */}
        <StepIndicator steps={steps} currentStep={currentStep} />

        {/* Main Content Card */}
        <Card className="mt-8">
          <CardContent className="p-8 min-h-[500px]">
            {currentStep === 1 && (
              <Step1ChooseConnector
                selected={wizardData.connector}
                onSelect={(connector) => updateWizardData('connector', connector)}
              />
            )}
            
            {currentStep === 2 && (
              <Step2Authenticate
                connector={wizardData.connector}
                auth={wizardData.auth}
                configId={wizardData.configId}
                authMethod={wizardData.authMethod}
                authComplete={wizardData.authComplete}
                onAuthChange={(auth) => updateWizardData('auth', auth)}
                onConfigCreated={(configId) => updateWizardData('configId', configId)}
                onAuthMethodChange={(method) => updateWizardData('authMethod', method)}
                onAuthComplete={(complete) => updateWizardData('authComplete', complete)}
              />
            )}

            
            {currentStep === 3 && (
              <Step3AccessScope
                connector={wizardData.connector}
                configId={wizardData.configId}
                scope={wizardData.scope}
                onChange={(scope) => updateWizardData('scope', scope)}
              />
            )}



            
            {currentStep === 4 && (
              <Step4Entities
                connector={wizardData.connector}
                selected={wizardData.entities}
                onChange={(entities) => updateWizardData('entities', entities)}
              />
            )}
            
            {currentStep === 5 && (
              <Step5Review
                data={wizardData}
                onConnect={handleConnect}
                onError={handleError}
              />
            )}
          </CardContent>

          {/* Footer Actions */}
          <div className="border-t border-[#2a2a2a] p-6 flex items-center justify-between">
            <Button
              variant="secondary"
              onClick={handleBack}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            {currentStep < 5 ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Next
              </Button>
            ) : (
              <div className="text-sm text-[#6a6a6a]">
                Click "Connect & Start Syncing" above
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
